import React, {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Relationship } from "@adsk/acc-reference-picker/es/types/types";
import { useLocale } from "@adsk/alloy-react-locale";

import i18n from "../i18n";
import {
  Asset,
  AssetDTO,
  AssetDuplicate,
  AssetsUserInfo,
  AssetUpdate,
  Category,
  CategoryDTO,
  CustomField,
  parseAsset,
  parseCategoryMap,
  StatusSetAssignment,
  StatusSetDTO,
} from "../models";
import { AssetModelAssociations, AssetSheetAssociations } from "../types";

export type DataCommonType = {
  assets: Record<AssetDTO["id"], AssetDTO>;
  assetIds: Asset["id"][];
  connectedEntityName: string;
  customFields: Record<Category["internalId"], CustomField[]>;
  // Reference-only, like `customFields`: `{ classificationTreeId }` per structure assigned to
  // (or inherited by) the category, no name/description (resolved live via the `treeInfo`
  // remote). Optional because not every consumer of this shared context supplies classification
  // data.
  classificationStructures?: Record<
    Category["internalId"],
    { classificationTreeId: string }[]
  >;
  onDeleteAssets: (shouldDeleteUnderlyingAsset: boolean) => void;
  onDrawerClose: () => void;
  onDuplicateAssets: (
    assetsToDuplicate: Record<Asset["id"], AssetDuplicate>
  ) => void;
  onUpdateAssets: (
    assetUpdates: Record<Asset["id"], AssetUpdate>,
    relationships?: Record<Relationship["id"], Relationship>
  ) => void;
  // Classification writes go through a dedicated per-asset endpoint (ADASSETS-1469), not
  // `onUpdateAssets`/PATCH — `nodeId` is the `nodePicker` remote's integer id; the host translates
  // it to the acc-classifications `externalId` the endpoint requires.
  onAssignClassificationValue?: (
    assetId: Asset["id"],
    classificationTreeId: string,
    nodeId: string
  ) => void;
  // Host-supplied translation between the `nodePicker` remote's integer node id and the stable
  // acc-classifications `externalId` `asset.classificationValues` is stored in — optional, like
  // `classificationStructures`, since not every consumer of this shared context edits
  // classifications.
  classificationNodeIdMap?: {
    toExternalId: (treeId: string, nodeId: string) => string | undefined;
    toNodeId: (treeId: string, externalId: string) => string | undefined;
    isLoading: boolean;
    // True when the last id-map fetch failed — treat like `isLoading` for disabling the field,
    // otherwise a translation that can't succeed silently discards the user's selection.
    isError: boolean;
  };
  projectId: string;
  shouldRenderDuplicateButton?: boolean;
  shouldRenderDeleteButton?: boolean;
  statusSetMap: Record<StatusSetDTO["id"], StatusSetDTO>;
  statusSetAssignmentsMap: Record<Category["internalId"], StatusSetAssignment>;
  assetsUserInfo?: AssetsUserInfo;
  assetSheetAssociations?: AssetSheetAssociations;
  assetModelAssociations?: AssetModelAssociations;
};
export interface DataProviderProps
  extends DataCommonType,
    PropsWithChildren<unknown> {
  categoryMap: Record<CategoryDTO["id"], CategoryDTO>;
  categoryMapByInternalId: Record<CategoryDTO["internalId"], CategoryDTO>;
  defaultStatusSet: StatusSetDTO;
  isSystemsEnabled?: boolean;
  isAssetQrCodeEnabled?: boolean;
  isPercentCompleteEnabled?: boolean;
}
interface DataContextType extends DataCommonType {
  asset: Asset | undefined;
  updateAsset: Dispatch<SetStateAction<Asset>>;
  categoryMap: Record<Category["id"], Category>;
  categoryMapByInternalId: Record<Category["internalId"], Category>;
  isSystemsEnabled?: boolean;
  isAssetQrCodeEnabled?: boolean;
  isPercentCompleteEnabled?: boolean;
}

export const DataContext = createContext<DataContextType>({
  asset: undefined,
  assetIds: [],
  assets: {},
  categoryMap: {},
  categoryMapByInternalId: {},
  connectedEntityName: "",
  customFields: {},
  classificationStructures: {},
  projectId: "",
  shouldRenderDuplicateButton: false,
  shouldRenderDeleteButton: false,
  statusSetMap: {},
  statusSetAssignmentsMap: {},
  assetsUserInfo: undefined,
  isSystemsEnabled: false,
  isAssetQrCodeEnabled: true,
  isPercentCompleteEnabled: false,
  onDeleteAssets: () => {},
  onDrawerClose: () => {},
  onDuplicateAssets: () => {},
  onUpdateAssets: () => {},
  onAssignClassificationValue: () => {},
  updateAsset: () => {},
  assetSheetAssociations: undefined,
  assetModelAssociations: undefined,
});

export const useData = () => {
  return useContext(DataContext) as DataContextType;
};

const DataProvider: FC<DataProviderProps> = ({
  assetIds,
  assets,
  children,
  categoryMap: categoriesDTO,
  categoryMapByInternalId: categoriesByInternalIdDTO,
  connectedEntityName,
  customFields,
  classificationStructures,
  onDeleteAssets,
  onDrawerClose,
  onDuplicateAssets,
  onUpdateAssets,
  onAssignClassificationValue,
  classificationNodeIdMap,
  projectId,
  shouldRenderDuplicateButton,
  shouldRenderDeleteButton,
  statusSetMap,
  statusSetAssignmentsMap,
  defaultStatusSet,
  assetsUserInfo,
  isSystemsEnabled,
  isAssetQrCodeEnabled,
  isPercentCompleteEnabled,
  assetSheetAssociations,
  assetModelAssociations,
}) => {
  const { T } = useLocale(i18n);

  if (!assetsUserInfo) {
    throw new Error(T.translate("errors.assetsUserInfoUndefined")?.toString());
  }
  if (!assetIds) {
    throw new Error(T.translate("errors.noAssetsSelected")?.toString());
  }

  const [detailAsset, setDetailAsset] = useState<Asset>(
    assets[assetIds[0]] && parseAsset(assets[assetIds[0]])
  );
  useEffect(() => {
    setDetailAsset(assets[assetIds[0]] && parseAsset(assets[assetIds[0]]));
  }, [assetIds, assets]);

  const combinedStatusSets = useMemo(() => {
    const statusSetsWithDefault = { ...statusSetMap };
    statusSetsWithDefault[defaultStatusSet["id"]] = defaultStatusSet;

    return statusSetsWithDefault;
  }, [defaultStatusSet, statusSetMap]);

  const categoryMap = useMemo(
    () => parseCategoryMap(categoriesDTO),
    [categoriesDTO]
  );

  const categoryMapByInternalId = useMemo(
    () => parseCategoryMap(categoriesByInternalIdDTO),
    [categoriesByInternalIdDTO]
  );

  const value = {
    asset: detailAsset,
    assetIds,
    assets,
    categoryMap,
    categoryMapByInternalId,
    connectedEntityName,
    customFields,
    classificationStructures,
    onDeleteAssets,
    onDrawerClose,
    onDuplicateAssets,
    onUpdateAssets,
    onAssignClassificationValue,
    classificationNodeIdMap,
    projectId,
    shouldRenderDuplicateButton,
    shouldRenderDeleteButton,
    statusSetMap: combinedStatusSets,
    statusSetAssignmentsMap,
    updateAsset: setDetailAsset,
    assetsUserInfo,
    isSystemsEnabled,
    isAssetQrCodeEnabled,
    isPercentCompleteEnabled,
    assetSheetAssociations,
    assetModelAssociations,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataProvider;
